// src/pages/admin/AuditLogsPage.tsx
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import { cn } from '@/lib/utils';
import Button from '@/components/ui/Button';
import { PageHeader } from '@/components/ui/PageHeader';
import { Panel } from '@/components/ui/Panel';
import { Segmented } from '@/components/ui/Segmented';
import { Badge, type BadgeTone } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { Table, THead, TBody, Tr, Th, Td, TableSkeleton } from '@/components/ui/Table';
import { fieldControlSm } from '@/components/ui/fieldStyles';

interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface SecurityLogRow {
  id: string;
  user_id: string | null;
  user_email: string | null;
  event_type: string;
  ip_address: string;
  user_agent: string | null;
  details: string | null;
  severity: string;
  timestamp: number;
}

interface UsageLogRow {
  id: string;
  user_id: string;
  user_email: string | null;
  token_id: string;
  token_name: string | null;
  token_prefix: string | null;
  api_service: string;
  endpoint: string;
  method: string;
  status_code: number | null;
  response_time: number | null;
  ip_address: string | null;
  key_environment: string;
  timestamp: number;
}

const SEVERITY_TONES: Record<string, BadgeTone> = {
  info: 'neutral',
  warning: 'warning',
  error: 'error',
  critical: 'error',
};

const STATUS_TONES: Record<string, BadgeTone> = {
  paid: 'success',
  pending: 'warning',
  overdue: 'warning',
  cancelled: 'error',
};

const fmtTime = (ts: number) => new Date(ts * 1000).toLocaleString();
const filterCls = cn(fieldControlSm, 'w-auto min-w-[10rem] pr-8');

const PaginationBar: React.FC<{ pagination?: Pagination; page: number; onPage: (p: number) => void }> = ({ pagination, page, onPage }) => {
  if (!pagination || pagination.pages <= 1) return null;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 pt-3 text-sm text-muted-foreground">
      <span>
        Page <span className="font-mono tabular-nums text-foreground">{pagination.page}</span> of{' '}
        <span className="font-mono tabular-nums text-foreground">{pagination.pages}</span> (<span className="font-mono tabular-nums">{pagination.total}</span> total)
      </span>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          Previous
        </Button>
        <Button size="sm" variant="outline" disabled={page >= pagination.pages} onClick={() => onPage(page + 1)}>
          Next
        </Button>
      </div>
    </div>
  );
};

const Filters: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex flex-wrap gap-2">{children}</div>
);

const SecurityLogsTab: React.FC = () => {
  const [page, setPage] = useState(1);
  const [eventType, setEventType] = useState('');
  const [severity, setSeverity] = useState('');

  const { data, isLoading } = useQuery<{ logs: SecurityLogRow[]; pagination: Pagination }>({
    queryKey: ['admin-security-logs', page, eventType, severity],
    queryFn: () =>
      apiClient
        .get('/admin/security/logs', { params: { page, limit: 20, event_type: eventType || undefined, severity: severity || undefined } })
        .then(r => r.data),
  });
  const logs = data?.logs ?? [];

  return (
    <Panel
      title="Sign-in and security events"
      action={
        <Filters>
          <select aria-label="Event type" className={filterCls} value={eventType} onChange={e => { setEventType(e.target.value); setPage(1); }}>
            <option value="">All event types</option>
            <option value="login_attempt">Login attempt</option>
            <option value="registration">Registration</option>
            <option value="logout">Logout</option>
            <option value="token_created">Token created</option>
            <option value="password_changed">Password changed</option>
            <option value="ip_whitelist_changed">IP whitelist changed</option>
            <option value="suspicious_activity">Suspicious activity</option>
            <option value="brute_force_detected">Brute force detected</option>
            <option value="rate_limit_exceeded">Rate limit exceeded</option>
            <option value="ip_blocked">IP blocked</option>
          </select>
          <select aria-label="Severity" className={filterCls} value={severity} onChange={e => { setSeverity(e.target.value); setPage(1); }}>
            <option value="">All severities</option>
            <option value="info">Info</option>
            <option value="warning">Warning</option>
            <option value="error">Error</option>
            <option value="critical">Critical</option>
          </select>
        </Filters>
      }
      bodyClassName="px-0 pb-3"
    >
      {isLoading ? (
        <TableSkeleton rows={6} />
      ) : logs.length === 0 ? (
        <EmptyState title="No events found." description="Try a different event type or severity." />
      ) : (
        <Table minWidth={720}>
          <THead>
            <tr><Th>Time</Th><Th>User</Th><Th>Event</Th><Th>Severity</Th><Th>IP</Th></tr>
          </THead>
          <TBody>
            {logs.map(log => (
              <Tr key={log.id}>
                <Td muted className="whitespace-nowrap">{fmtTime(log.timestamp)}</Td>
                <Td>{log.user_email ?? 'None'}</Td>
                <Td mono className="text-xs">{log.event_type}</Td>
                <Td><Badge tone={SEVERITY_TONES[log.severity] ?? 'neutral'}>{log.severity}</Badge></Td>
                <Td mono muted className="text-xs">{log.ip_address}</Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
      <PaginationBar pagination={data?.pagination} page={page} onPage={setPage} />
    </Panel>
  );
};

const UsageLogsTab: React.FC = () => {
  const [page, setPage] = useState(1);
  const [apiService, setApiService] = useState('');

  const { data, isLoading } = useQuery<{ logs: UsageLogRow[]; pagination: Pagination }>({
    queryKey: ['admin-usage-logs', page, apiService],
    queryFn: () =>
      apiClient
        .get('/admin/usage/logs', { params: { page, limit: 20, api_service: apiService || undefined } })
        .then(r => r.data),
  });
  const logs = data?.logs ?? [];

  return (
    <Panel
      title="API key usage"
      action={
        <Filters>
          <select aria-label="Service" className={filterCls} value={apiService} onChange={e => { setApiService(e.target.value); setPage(1); }}>
            <option value="">All services</option>
            <option value="route">Maps Routing</option>
            <option value="render">Maps Tile Rendering</option>
            <option value="geocode">Lookup Geocoding</option>
            <option value="convert">Lookup Address Convert</option>
            <option value="reverse">Lookup Reverse Geocoding</option>
            <option value="resolve">Lookup Postal Resolve</option>
          </select>
        </Filters>
      }
      bodyClassName="px-0 pb-3"
    >
      {isLoading ? (
        <TableSkeleton rows={6} />
      ) : logs.length === 0 ? (
        <EmptyState title="No requests found." description="Requests made with any API key show up here." />
      ) : (
        <Table minWidth={880}>
          <THead>
            <tr><Th>Time</Th><Th>User</Th><Th>Key</Th><Th>Endpoint</Th><Th align="right">Status</Th><Th align="right">Latency</Th><Th>IP</Th></tr>
          </THead>
          <TBody>
            {logs.map(log => (
              <Tr key={log.id}>
                <Td muted className="whitespace-nowrap">{fmtTime(log.timestamp)}</Td>
                <Td>{log.user_email ?? 'None'}</Td>
                <Td mono className="whitespace-nowrap text-xs">{log.token_name ?? 'None'} <span className="text-muted-foreground">({log.token_prefix ?? 'None'})</span></Td>
                <Td mono className="text-xs"><span className="text-muted-foreground">{log.method}</span> {log.endpoint}</Td>
                <Td align="right" mono>
                  <span className={log.status_code && log.status_code >= 400 ? 'text-error' : 'text-success'}>
                    {log.status_code ?? 'None'}
                  </span>
                </Td>
                <Td align="right" mono muted>{log.response_time != null ? `${log.response_time}ms` : 'None'}</Td>
                <Td mono muted className="text-xs">{log.ip_address ?? 'None'}</Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
      <PaginationBar pagination={data?.pagination} page={page} onPage={setPage} />
    </Panel>
  );
};

interface TransactionRow {
  id: string;
  user_id: string;
  user_email: string | null;
  invoice_number: string;
  amount: number;
  credits_used: number;
  status: string;
  paystack_invoice_id: string | null;
  paid_at: number | null;
  created_at: number;
}

const TransactionsTab: React.FC = () => {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');

  const { data, isLoading } = useQuery<{ transactions: TransactionRow[]; pagination: Pagination }>({
    queryKey: ['admin-transactions', page, status],
    queryFn: () =>
      apiClient
        .get('/admin/transactions', { params: { page, limit: 20, status: status || undefined } })
        .then(r => r.data),
  });
  const rows = data?.transactions ?? [];

  return (
    <Panel
      title="Transactions"
      action={
        <Filters>
          <select aria-label="Status" className={filterCls} value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
            <option value="">All statuses</option>
            <option value="paid">Paid</option>
            <option value="pending">Pending</option>
            <option value="overdue">Overdue</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </Filters>
      }
      bodyClassName="px-0 pb-3"
    >
      {isLoading ? (
        <TableSkeleton rows={6} />
      ) : rows.length === 0 ? (
        <EmptyState title="No transactions found." description="Payments for credits show up here." />
      ) : (
        <Table minWidth={820}>
          <THead>
            <tr><Th>Created</Th><Th>User</Th><Th>Reference</Th><Th align="right">Amount</Th><Th align="right">Credits</Th><Th>Status</Th><Th>Paid</Th></tr>
          </THead>
          <TBody>
            {rows.map(tx => (
              <Tr key={tx.id}>
                <Td muted className="whitespace-nowrap">{fmtTime(tx.created_at)}</Td>
                <Td>{tx.user_email ?? 'None'}</Td>
                <Td mono className="text-xs">{tx.invoice_number}</Td>
                <Td align="right" mono>{(tx.amount / 100).toFixed(2)}</Td>
                <Td align="right" mono>{tx.credits_used}</Td>
                <Td><Badge tone={STATUS_TONES[tx.status] ?? 'warning'}>{tx.status}</Badge></Td>
                <Td muted className="whitespace-nowrap">{tx.paid_at ? fmtTime(tx.paid_at) : 'Not paid'}</Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
      <PaginationBar pagination={data?.pagination} page={page} onPage={setPage} />
    </Panel>
  );
};

type Tab = 'security' | 'usage' | 'transactions';
const TABS: { value: Tab; label: string }[] = [
  { value: 'security', label: 'Sign-in and security' },
  { value: 'usage', label: 'API key usage' },
  { value: 'transactions', label: 'Transactions' },
];

const AuditLogsPage: React.FC = () => {
  const [tab, setTab] = useState<Tab>('security');

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <PageHeader
        title="Audit logs"
        description="Sign-ins and security events, API key requests and payments, across every account."
      />
      <div className="max-w-full overflow-x-auto">
        <Segmented label="Log" value={tab} options={TABS} onChange={setTab} />
      </div>
      {tab === 'security' && <SecurityLogsTab />}
      {tab === 'usage' && <UsageLogsTab />}
      {tab === 'transactions' && <TransactionsTab />}
    </div>
  );
};

export default AuditLogsPage;
